import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Flex, Box } from 'reflexbox';
import { ButtonCircle, Text, Badge } from 'rebass';

import SimcInput, { COMMANDS_APL } from 'shared/simcinput';

import actions from 'frontend/state/actions';

import { selectors as simbotSelectors } from 'frontend/state/reducers/simbot';
import { selectors as customAplsSelectors } from 'frontend/state/reducers/simbot/customApls';

import config from 'frontend/config';
import SvgIcons from 'frontend/SvgIcons';

import {
  Icon,
  If,
  Textarea,
  ToggleView,
} from 'frontend/components';

import SimcExpertOptions from './SimcExpertOptions';

const CustomAplOptions = ({ open, className, spec, apl, invalidCommands, inputCustomApl, onLabelClick }) => {
  let details;
  const style = {
    fontSize: '16px',
    opacity: 0.7,
  };
  const hasInvalidCommands = invalidCommands.length > 0;
  if (hasInvalidCommands) {
    details = <Badge theme="error" style={{ fontSize: '14px' }}>Invalid Commands</Badge>;
  } else if (apl) {
    details = <Badge theme="warning" style={{ fontSize: '14px' }}>Custom</Badge>;
  } else {
    details = <span style={style}>SimC Defaults</span>;
  }
  const label = (
    <Flex align="center">
      <Box mr={1}>
        <If
          condition={hasInvalidCommands}
          then={() => (
            <Icon name="warning" color={config.colors.red} size={26} />
          )}
          else={() => (
            <SvgIcons.apl.small
              style={{
                fill: config.colors.iconInfo,
                width: 26,
                height: 26,
              }}
            />
          )}
        />
      </Box>
      <Box mr={1}>Custom APL and SimC Options:</Box>
      <Box> {details}</Box>
    </Flex>
  );
  return (
    <Box>
      <ToggleView
        label={label}
        onClick={onLabelClick}
        open={open}
      >
        <Box sm={12} md={10} ml={2} pl={2} style={{ borderLeft: `1px solid ${config.colors.midgray}` }}>
          <If
            condition={className && spec}
            then={() => (
              <Box>
                <Textarea
                  name="apl"
                  label={`${spec} ${className}`}
                  value={apl}
                  onChange={e => inputCustomApl(className, spec, e.target.value)}
                  rows={5}
                  mb={0}
                />
                <Text small mt={0}>
                  Use a
                  {' '}
                  <a
                    href="https://github.com/simulationcraft/simc/wiki/ActionLists"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    custom APL
                  </a>
                  {' '}
                  or
                  {' '}
                  <a
                    href="https://github.com/simulationcraft/simc/wiki/ExpansionOptions"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    SimC expansion options
                  </a>
                </Text>
                <If
                  condition={invalidCommands.length > 0}
                  then={() => (
                    <>
                      <Flex align="center" my={1}>
                        <Box>
                          <ButtonCircle
                            size={24}
                            color={config.colors.black}
                            backgroundColor={config.colors.warning}
                            style={{
                              minHeight: '24px',
                              lineHeight: '24px',
                            }}
                            mr={1}
                          >
                            <Icon name="warning" size={16} />
                          </ButtonCircle>
                        </Box>
                        <Box>
                          <strong>Invalid or unsupported options:</strong> {invalidCommands.join(', ')}
                        </Box>
                      </Flex>
                      <Text small>
                        These options are either not supported by Raidbots or you have copied your /simc input here
                        by mistake.
                      </Text>
                    </>
                  )}
                />
                <ToggleView
                  label={<Text small>Supported Options</Text>}
                  defaultOpen={false}
                  hideClickToOpenText
                >
                  <pre>{_.join(_.sortBy(COMMANDS_APL), '\n')}</pre>
                </ToggleView>
                <Box mt={2}>
                  <SimcExpertOptions />
                </Box>
              </Box>
            )}
            else={() => (
              <Text>No character loaded</Text>
            )}
          />
        </Box>
      </ToggleView>
    </Box>
  );
};

CustomAplOptions.propTypes = {
  open: PropTypes.bool,
  className: PropTypes.string,
  spec: PropTypes.string,
  apl: PropTypes.string,
  invalidCommands: PropTypes.arrayOf(PropTypes.string),
  onLabelClick: PropTypes.func,
  inputCustomApl: PropTypes.func,
};

CustomAplOptions.defaultProps = {
  open: false,
  className: null,
  spec: null,
  apl: null,
  invalidCommands: [],
  onLabelClick: null,
  inputCustomApl: null,
};

const mapStateToProps = (state, props) => ({
  apl: simbotSelectors.apl(state, props),
  className: simbotSelectors.characterClassName(state, props),
  spec: simbotSelectors.characterSpec(state, props),
  open: customAplsSelectors.open(state),
});

const mapDispatchToProps = {
  inputCustomApl: actions.inputCustomApl,
  onLabelClick: actions.toggleCustomApls,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  // parse APL for errors / invalid commands
  const aplData = SimcInput.parse(stateProps.apl);
  const invalidCommands = _.map(SimcInput.getInvalidAplCommands(aplData), item => item.key);

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    invalidCommands,
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(CustomAplOptions);
